import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduTimetableAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { CalendarClock, Plus, Trash2, Loader2, BookOpen, DoorOpen, Gauge, Users, ArrowRight } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];
const EMPTY_SLOT = {
  term_id: 1, course_id: '', faculty_profile_id: '', room_id: '',
  day_of_week: 0, period: 1, section: '',
};

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationTimetableManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [slots, setSlots] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [termId, setTermId] = useState(1);

  const [slotDialog, setSlotDialog] = useState(false);
  const [slotForm, setSlotForm] = useState(EMPTY_SLOT);

  // Courses & rooms are managed on their own pages; here we only need them to populate
  // the schedule dropdowns and to label grid cells.
  const loadCatalogues = async () => {
    setLoading(true);
    try {
      const [cRes, rRes] = await Promise.all([eduTimetableAPI.listCourses(), eduTimetableAPI.listRooms()]);
      setCourses(toArray(cRes));
      setRooms(toArray(rRes));
    } catch (err) {
      if (err?.response?.status === 402) { setCourses([]); setRooms([]); }
      else Swal.fire('Could not load timetable data', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTerm = async () => {
    try {
      const [sRes, wRes] = await Promise.all([
        eduTimetableAPI.listSlots({ term_id: termId }),
        eduTimetableAPI.workloadSummary(termId),
      ]);
      setSlots(toArray(sRes));
      setWorkload(toArray(wRes));
    } catch (err) {
      if (err?.response?.status !== 402) { setSlots([]); setWorkload([]); }
    }
  };

  useEffect(() => { loadCatalogues(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadTerm(); }, [termId]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => ({
    courses: courses.length,
    rooms: rooms.length,
    slots: slots.length,
    faculty: workload.length,
  }), [courses, rooms, slots, workload]);

  const courseById = useMemo(() => {
    const m = {};
    courses.forEach((c) => { m[c.id] = c; });
    return m;
  }, [courses]);

  const slotAt = (day, period) => slots.find((s) => s.day_of_week === day && s.period === period);
  const setSlotField = (k, v) => setSlotForm((p) => ({ ...p, [k]: v }));

  const openCreateSlot = (day, period) => {
    setSlotForm({ ...EMPTY_SLOT, term_id: termId, day_of_week: day, period });
    setSlotDialog(true);
  };

  const submitSlot = async () => {
    if (!slotForm.course_id) return Swal.fire('Course required', 'Select a course for this slot.', 'warning');
    const payload = {
      term_id: Number(slotForm.term_id) || termId,
      course_id: Number(slotForm.course_id),
      faculty_profile_id: slotForm.faculty_profile_id ? Number(slotForm.faculty_profile_id) : null,
      room_id: slotForm.room_id ? Number(slotForm.room_id) : null,
      day_of_week: Number(slotForm.day_of_week),
      period: Number(slotForm.period),
      section: slotForm.section || null,
    };
    try {
      await eduTimetableAPI.createSlot(payload);
      setSlotDialog(false);
      await loadTerm();
      Swal.fire({ icon: 'success', title: 'Slot added', timer: 1200, showConfirmButton: false });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Please try again.';
      Swal.fire(err?.response?.status === 409 ? 'Scheduling conflict' : 'Save failed', msg, 'error');
    }
  };

  const removeSlot = async (slot) => {
    const ok = await Swal.fire({ icon: 'warning', title: 'Remove slot?', showCancelButton: true, confirmButtonText: 'Remove', confirmButtonColor: '#b5456b' });
    if (!ok.isConfirmed) return;
    try {
      await eduTimetableAPI.deleteSlot(slot.id);
      await loadTerm();
    } catch (err) {
      Swal.fire('Remove failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-indigo-600" /> Timetable &amp; Workload
          </h2>
          <p className="text-sm text-muted-foreground">
            A conflict-free weekly grid and per-faculty workload utilisation. Manage the course and room catalogues on their own pages.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/edu-courses')}>
            <BookOpen className="h-4 w-4 mr-1" /> Manage Courses <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/edu-rooms')}>
            <DoorOpen className="h-4 w-4 mr-1" /> Manage Rooms <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Term</Label>
            <Input type="number" className="w-20" value={termId} onChange={(e) => setTermId(Number(e.target.value) || 1)} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Courses" value={stats.courses} icon={BookOpen} />
        <StatCard label="Rooms" value={stats.rooms} icon={DoorOpen} />
        <StatCard label="Scheduled Slots" value={stats.slots} icon={CalendarClock} />
        <StatCard label="Faculty Scheduled" value={stats.faculty} icon={Users} />
      </div>

      {/* Timetable grid */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Timetable — Term {termId}</CardTitle>
          <CardDescription>Click an empty cell to schedule a class. Rooms and faculty are kept conflict-free.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : courses.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="Add courses first"
              description="Create or import courses and rooms before building the timetable."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    {DAYS.map((d) => <TableHead key={d}>{d}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {PERIODS.map((period) => (
                    <TableRow key={period}>
                      <TableCell className="font-medium">P{period}</TableCell>
                      {DAYS.map((_, day) => {
                        const slot = slotAt(day, period);
                        const course = slot ? courseById[slot.course_id] : null;
                        return (
                          <TableCell key={day} className="align-top">
                            {slot ? (
                              <div className="rounded-md border p-1.5 text-xs space-y-0.5">
                                <div className="font-medium">{slot.course_code || course?.code || `#${slot.course_id}`}</div>
                                {slot.room_name && <div className="text-muted-foreground">{slot.room_name}</div>}
                                {slot.is_substitute && <Badge variant="outline">Sub</Badge>}
                                {canManage && (
                                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeSlot(slot)} title="Remove">
                                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                                  </Button>
                                )}
                              </div>
                            ) : canManage ? (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openCreateSlot(day, period)} title="Add class">
                                <Plus className="h-4 w-4" />
                              </Button>
                            ) : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Workload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Gauge className="h-5 w-5" /> Workload Summary — Term {termId}</CardTitle>
          <CardDescription>Teaching hours vs. the workload norm for each scheduled faculty member.</CardDescription>
        </CardHeader>
        <CardContent>
          {workload.length === 0 ? (
            <EmptyState icon={Gauge} title="No workload yet" description="Schedule classes to a faculty member to see utilisation." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Faculty Profile</TableHead>
                    <TableHead>Teaching Hours</TableHead>
                    <TableHead>Max Weekly Hours</TableHead>
                    <TableHead>Utilisation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workload.map((row) => (
                    <TableRow key={row.faculty_profile_id}>
                      <TableCell className="font-medium">#{row.faculty_profile_id}</TableCell>
                      <TableCell>{row.teaching_hours}</TableCell>
                      <TableCell>{row.max_weekly_hours || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={row.utilization_pct > 100 ? 'destructive' : 'secondary'}>
                          {row.utilization_pct}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Slot dialog */}
      <Dialog open={slotDialog} onOpenChange={setSlotDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Schedule Class — {DAYS[slotForm.day_of_week]} · P{slotForm.period}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Course *</Label>
                <Select value={slotForm.course_id ? String(slotForm.course_id) : ''} onValueChange={(v) => setSlotField('course_id', v)}>
                  <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.code} · {c.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Room</Label>
                <Select value={slotForm.room_id ? String(slotForm.room_id) : ''} onValueChange={(v) => setSlotField('room_id', v)}>
                  <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                  <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Faculty Profile ID</Label>
                <Input type="number" value={slotForm.faculty_profile_id} onChange={(e) => setSlotField('faculty_profile_id', e.target.value)} placeholder="e.g. 12" />
              </div>
              <div className="space-y-1">
                <Label>Section</Label>
                <Input value={slotForm.section || ''} onChange={(e) => setSlotField('section', e.target.value)} placeholder="A" />
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotDialog(false)}>Cancel</Button>
            <Button onClick={submitSlot}>Add to timetable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationTimetableManagement;
